import { useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { useLoginState, useFeatures, useExperiment } from '@xing-com/hub';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconCompanies,
  IconCompaniesFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconWeOffer,
  IconJobsFilled,
  IconMessages,
  IconMessagesFilled,
  IconNews,
  IconNewsFilled,
  IconNotificationFilled,
} from '@xing-com/icons';
import { Section } from '@xing-com/layout-primitives';
import {
  ProductsMenuWithItemsLoggedOut,
  ProductsMenuWithItemsLoggedIn,
} from '@xing-com/platform-layout-products-menu';
import { SkipNavigation } from '@xing-com/platform-layout-skip-navigation';
import { TipBoxPreset } from '@xing-com/tip-box';
import { Toggle } from '@xing-com/toggle';
import { spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { ActivityCenterButton } from './activity-center-button/activity-center-button';
import { DiscountBadge } from './discount-badge';
import {
  StyledOuterActionsContainer,
  StyledHeader,
  StyledHeaderPlacebo,
  StyledHeaderWrapper,
  StyledLogo,
  StyledLogoWrapper,
  StyledButterfly,
  StyledOnlyOnBigScreenContainer,
  StyledProductsMenuWrapper,
  ActionButtonSpace,
  BoneyardWithDiscountContainer,
} from './header.styles';
import { LoginButton } from './login-button/login-button';
import { MeMenuDropdown } from './me-menu-dropdown';
import { NavItemHeader } from './nav-item-header';
import { NotificationCenterIcon } from './notification-center-icon';
import { SearchDropdown } from './search-dropdown';
import { Boneyard } from './upsell-boneyard/boneyard';

const HeaderLogo: FC = () => {
  return (
    <StyledLogoWrapper>
      <StyledLogo dataTestId="logo" />
      <StyledButterfly dataTestId="logoMobile" />
    </StyledLogoWrapper>
  );
};

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

export type HeaderProps = {
  className?: string;
  loginLink?: string;
  loginTracking?: { sc_o: string };
};

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spaceS};
  align-items: center;
`;

export const Header: React.FC<HeaderProps> = ({ className, loginLink }) => {
  const { formatMessage } = useIntl();
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';

  const focusViewLOEnabled = useExperiment('ABACUS-507') === 'B';

  const {
    showUpsellHint,
    showJobSeekerBoneyardUpsellHint,
    sixtFocusmodeAvailable,
    sixtFocusmodeState,
  } = useFeatures()?.data || {};

  const [isFirstTime, setIsFirstTime] = useLocalStorage(
    'jop-focus-tip-box',
    true
  );

  const [focusViewMode, setFocusViewMode] = useState(
    sixtFocusmodeState || false
  );
  const isFocusViewEnabled = sixtFocusmodeAvailable && focusViewMode;

  const [showTipBox, setShowTipBox] = useState(isFirstTime);

  const handleToggleChange = (): void => {
    setShowTipBox(false);
    setFocusViewMode(!focusViewMode);
  };

  const onOpenChange = (open: boolean): void => {
    setIsFirstTime(false);

    if (!open) {
      setShowTipBox(false);
    }
  };

  const onClickTipbox = (): void => {
    setIsFirstTime(false);
    setShowTipBox(false);
    setFocusViewMode(!focusViewMode);
  };

  const onDisableAutofocus = (e: Event): void => {
    e.preventDefault();
  };

  return (
    <StyledHeaderPlacebo $isLoggedOut={isLoggedOut} className={className}>
      <StyledHeaderWrapper>
        <SkipNavigation />
        <Section noBlockPadding>
          <StyledHeader>
            <HeaderLogo />
            <StyledOuterActionsContainer
              aria-label="header-navigation"
              data-testid="header-navigation"
              as="nav"
            >
              {isLoggedOut ? (
                <>
                  <StyledOnlyOnBigScreenContainer>
                    <NavItemHeader
                      activeItemValues={['jobs']}
                      icon={IconJobs}
                      activeIcon={IconJobsFilled}
                      title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
                      href="https://www.xing.com/jobs"
                      tracking={{
                        nwt_nav: 'find_jobs',
                        sc_o: 'navigation_find_jobs',
                      }}
                    />

                    {focusViewLOEnabled ? (
                      <NavItemHeader
                        activeItemValues={['jobs_easy_create']}
                        icon={IconWeOffer}
                        activeIcon={IconJobsFilled}
                        title={formatMessage({
                          id: 'MAINNAV_JOBS_EASY_CREATE',
                        })}
                        href="https://www.xing.com/recruiting/jobs/create"
                        tracking={{
                          //@TODO clarify if this is correct
                          nwt_nav: 'jobs_easy_create',
                          sc_o: 'navigation_jobs_easy_create',
                          sc_cmp: 'oowcbc0e3c5',
                        }}
                      />
                    ) : (
                      <>
                        <NavItemHeader
                          activeItemValues={['companies']}
                          icon={IconCompanies}
                          activeIcon={IconCompaniesFilled}
                          title={formatMessage({ id: 'MAINNAV_COMPANIES' })}
                          href="https://www.xing.com/companies"
                          tracking={{
                            nwt_nav: 'companies',
                            sc_o: 'navigation_companies',
                          }}
                        />
                        <NavItemHeader
                          icon={IconContacts}
                          activeIcon={IconContactsFilled}
                          title={formatMessage({ id: 'MAINNAV_NETWORK' })}
                          href="https://www.xing.com/network"
                          activeItemValues={['members']}
                          tracking={{
                            nwt_nav: 'network',
                            sc_o: 'navigation_network',
                          }}
                        />
                        <NavItemHeader
                          icon={IconNews}
                          activeIcon={IconNewsFilled}
                          title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
                          href="https://www.xing.com/insights"
                          activeItemValues={['insights']}
                          tracking={{
                            nwt_nav: 'insights',
                            sc_o: 'navigation_insights',
                          }}
                        />
                      </>
                    )}
                  </StyledOnlyOnBigScreenContainer>
                  <ActivityCenterButton
                    title={formatMessage({ id: 'MAINNAV_ACTIVITY_CENTER' })}
                    href="https://www.xing.com/activity?sc_o=navigation_activity"
                  />
                  <ActionButtonSpace>
                    <LoginButton loginLink={loginLink} />
                  </ActionButtonSpace>
                  <StyledOnlyOnBigScreenContainer>
                    <ProductsMenuWithItemsLoggedOut />
                  </StyledOnlyOnBigScreenContainer>
                </>
              ) : (
                <>
                  <NavItemHeader
                    activeItemValues={['jobs']}
                    icon={IconJobs}
                    activeIcon={IconJobsFilled}
                    title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
                    href="/jobs/find"
                    tracking={{
                      nwt_nav: 'find_jobs',
                      sc_o: 'navigation_find_jobs',
                    }}
                  />
                  <NavItemHeader
                    activeItemValues={['my-jobs']}
                    icon={IconBookmark}
                    activeIcon={IconBookmarkFilled}
                    title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
                    href="/jobs/my-jobs"
                    tracking={{
                      nwt_nav: 'your_jobs',
                      sc_o: 'navigation_your_jobs',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_jobs',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  {isFocusViewEnabled ? null : (
                    <NavItemHeader
                      icon={IconContacts}
                      activeIcon={IconContactsFilled}
                      title={formatMessage({ id: 'MAINNAV_NETWORK' })}
                      href="/network"
                      activeItemValues={['members']}
                      tracking={{
                        nwt_nav: 'network',
                        sc_o: 'navigation_network',
                        sc_o_PropActionOrigin: [
                          'navigation_badge_network',
                          NAVIGATION_BADGE_NO_BADGE,
                        ],
                      }}
                    />
                  )}
                  {isFocusViewEnabled ? null : (
                    <NavItemHeader
                      icon={IconNews}
                      activeIcon={IconNewsFilled}
                      title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
                      href="/discover/updates"
                      activeItemValues={['disco']}
                      tracking={{
                        nwt_nav: 'insights',
                        sc_o: 'navigation_insights',
                        sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                      }}
                    />
                  )}
                  <NavItemHeader
                    icon={IconMessages}
                    activeIcon={IconMessagesFilled}
                    title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
                    href="/chats"
                    activeItemValues={['messages']}
                    notificationKey="unreadChats"
                    tracking={{
                      nwt_nav: 'messages',
                      sc_o: 'navigation_messages',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_messages',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  <NavItemHeader
                    icon={NotificationCenterIcon}
                    activeIcon={IconNotificationFilled}
                    provideIconContext
                    activeItemValues={['supi']}
                    notificationKey="ncWeb"
                    href="/notifications"
                    title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
                    tracking={{
                      nwt_nav: 'notifications',
                      sc_o: 'navigation_notifications',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_notifications',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  {isFocusViewEnabled ? null : <SearchDropdown />}
                  <MeMenuDropdown />
                  {!showUpsellHint ? null : showJobSeekerBoneyardUpsellHint ? (
                    <BoneyardWithDiscountContainer>
                      <Boneyard
                        dataTestId="upsell-button"
                        size="small"
                        variant="primary"
                        href="/purchase/projobs?reagent=uplt_277&sc_o=navigation_projobs_cta"
                      >
                        {formatMessage({
                          id: 'FRAME_HEADER_UPSELL_PROJOBS',
                        })}
                      </Boneyard>
                      <DiscountBadge product={ProductType.Projobs} />
                    </BoneyardWithDiscountContainer>
                  ) : (
                    <BoneyardWithDiscountContainer>
                      <Boneyard
                        dataTestId="upsell-button"
                        size="small"
                        variant="primary"
                        href="/purchase/premium?reagent=uplt_95&sc_o=navigation_premium_cta"
                      >
                        {formatMessage({
                          id: 'FRAME_HEADER_UPSELL_PREMIUM',
                        })}
                      </Boneyard>
                      <DiscountBadge product={ProductType.Premium} />
                    </BoneyardWithDiscountContainer>
                  )}

                  {isFocusViewEnabled ? null : (
                    <StyledProductsMenuWrapper>
                      <ProductsMenuWithItemsLoggedIn />
                    </StyledProductsMenuWrapper>
                  )}

                  {sixtFocusmodeAvailable && (
                    <>
                      <ToggleWrapper>
                        <Toggle
                          on={focusViewMode}
                          onChange={() => handleToggleChange()}
                        />
                        <BodyCopy size="small" noMargin>
                          {formatMessage({
                            id: 'MAINNAV_JOB_FOCUS_TOGGLE',
                          })}
                        </BodyCopy>
                        <TipBoxPreset
                          align="end"
                          bodyText={formatMessage({
                            id: sixtFocusmodeState
                              ? 'TIPBOX_JOB_FOCUS_ON_DESCRIPTION'
                              : 'TIPBOX_JOB_FOCUS_DESCRIPTION',
                          })}
                          buttonLabel={formatMessage({
                            id: sixtFocusmodeState
                              ? 'TIPBOX_JOB_FOCUS_ON_BUTTON_LABEL'
                              : 'TIPBOX_JOB_FOCUS_BUTTON_LABEL',
                          })}
                          headlineText={formatMessage({
                            id: sixtFocusmodeState
                              ? 'TIPBOX_JOB_FOCUS_ON_TITLE'
                              : 'TIPBOX_JOB_FOCUS_TITLE',
                          })}
                          {...(sixtFocusmodeState
                            ? {
                                markerText: formatMessage({
                                  id: 'TIPBOX_JOB_FOCUS_MARKER',
                                }),
                              }
                            : {})}
                          showArrow
                          open={showTipBox}
                          onOpenChange={onOpenChange}
                          onOpenAutoFocus={onDisableAutofocus}
                          buttonProps={{
                            onClick: onClickTipbox,
                          }}
                          showClosingIndicator
                          side="bottom"
                          variant={
                            sixtFocusmodeState ? 'default' : 'highlighted'
                          }
                          trigger={<div></div>}
                        />
                      </ToggleWrapper>
                    </>
                  )}
                </>
              )}
            </StyledOuterActionsContainer>
          </StyledHeader>
        </Section>
      </StyledHeaderWrapper>
    </StyledHeaderPlacebo>
  );
};
